import React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

export function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Kullanıcının girdiği metni TR cep telefonu için 10 haneye normalize eder.
 * Kabul edilen girişler: +905..., +90 5..., 905..., 05..., 5... (rakam dışı karakterler temizlenir).
 * Sonuç: en fazla 10 hane, 5 ile başlar (5xxxxxxxxx) ya da boş string.
 */
export function normalizeTurkishMobileTo10Digits(raw: string): string {
  let digits = digitsOnly(raw);
  if (!digits.length) return '';

  // Sadece ülke kodu girildiyse (örn: "+90") state'i boşalt.
  if (digits === '90') return '';
  // Sadece sabit prefix ("+90 5") kaldıysa optional alanı boş kabul et.
  // Bu sayede kullanıcı yanlış numarayı silip prefix'i bırakınca
  // UI tarafında "zorunlu/invalid" hata göstermiyoruz.
  if (digits === '905') return '';

  if (digits.length > 10) {
    if (digits.startsWith('90') && digits[2] === '5') digits = digits.slice(2, 12);
    else if (digits.startsWith('0') && digits[1] === '5') digits = digits.slice(1, 11);
    else digits = digits.slice(0, 10);
  }

  if (digits.length && digits[0] !== '5') {
    if (digits.startsWith('0') && digits[1] === '5') digits = digits.slice(1);
    else if (digits.startsWith('90') && digits[2] === '5') digits = digits.slice(2);
    else digits = '5' + digits.slice(0, 9);
  }

  return digits.slice(0, 10);
}

/** Gösterim formatı: +90 5XX XXX XX XX */
export function formatTurkishMobileDisplay(digits10: string): string {
  const d = digitsOnly(digits10).slice(0, 10);
  if (!d.length) return '';
  const after5 = d[0] === '5' ? d.slice(1) : d;
  if (after5.length <= 2) return `+90 5${after5}`;
  if (after5.length <= 5) return `+90 5${after5.slice(0, 2)} ${after5.slice(2)}`;
  if (after5.length <= 7) return `+90 5${after5.slice(0, 2)} ${after5.slice(2, 5)} ${after5.slice(5)}`;
  return `+90 5${after5.slice(0, 2)} ${after5.slice(2, 5)} ${after5.slice(5, 7)} ${after5.slice(7, 9)}`;
}

/** 10 haneli (5xxxxxxxxx) TR cep numarası mı */
export function isValidTurkishMobileDigits10(digits10: string): boolean {
  return /^5\d{9}$/.test(digitsOnly(digits10).slice(0, 10));
}

/** 10 haneli 5 ile başlayan TR cep numarası -> API için 905xxxxxxxxx */
export function toApiPhone90(digits10: string): string {
  const d = digitsOnly(digits10).slice(0, 10);
  if (!/^5\d{9}$/.test(d)) return '';
  return '90' + d;
}

export type TurkishMobilePhoneInputProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  /** Sadece rakam olacak şekilde 10 hane (5xxxxxxxxx) ya da boş */
  valueDigits: string;
  /** Güncellenmiş 10 haneli digit string */
  onChangeDigits: (digits10: string) => void;
  /** Boşken input içinde gösterilecek değer */
  emptyDisplayValue?: string;
};

const FIXED_PREFIX = '+90 5';

const TurkishMobilePhoneInput: React.FC<TurkishMobilePhoneInputProps> = ({
  valueDigits,
  onChangeDigits,
  emptyDisplayValue = FIXED_PREFIX,
  inputProps,
  placeholder = FIXED_PREFIX,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
   const lastCaretRef = React.useRef<number | null>(null);

  const displayValue = valueDigits ? formatTurkishMobileDisplay(valueDigits) : emptyDisplayValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const prevDigits = valueDigits || '';
    let raw = e.target.value || '';
    let caret = e.target.selectionStart ?? raw.length;

    const prefixLen = FIXED_PREFIX.length;

    // Kullanıcının prefix'i (+90 5) değiştirmesine izin verme
    if (!raw.startsWith(FIXED_PREFIX)) {
      const afterPrefix = raw.slice(Math.min(raw.length, prefixLen));
      raw = FIXED_PREFIX + afterPrefix;
    }

    // 10 hane zaten doluyken herhangi bir yerde fazladan rakam yazıldığında
    // mevcut numarayı ezme; girişleri yok say.
    if (prevDigits.length >= 10) {
      const prevDisplay = displayValue || FIXED_PREFIX;
      const prevDigitsCount = digitsOnly(prevDisplay).length;
      const newDigitsCount = digitsOnly(raw).length;

      // Sadece rakam sayısı artıyorsa (yeni input) engelle.
      if (newDigitsCount > prevDigitsCount) {
        window.requestAnimationFrame(() => {
          if (inputRef.current) {
            try {
              // Fazladan input'u yok sayarken caret'i eski konumunda tut.
              const prevCaret = lastCaretRef.current ?? caret;
              const safePos = Math.max(prevCaret, FIXED_PREFIX.length);
              inputRef.current.setSelectionRange(safePos, safePos);
            } catch {
              // ignore
            }
          }
        });
        return;
      }
    }

    // Caret'in prefix'in soluna geçmesini engelle
    if (caret < prefixLen) {
      caret = prefixLen;
    }

    // Caret'ten önceki kısmı ayrı normalize edip, sonrasında caret pozisyonunu buna göre hesaplıyoruz
    const digitsBeforeCaret = normalizeTurkishMobileTo10Digits(raw.slice(0, caret));
    const allDigits = normalizeTurkishMobileTo10Digits(raw);

    onChangeDigits(allDigits);

    const formattedBeforeCaret = digitsBeforeCaret ? formatTurkishMobileDisplay(digitsBeforeCaret) : '';
    const nextPos =
      formattedBeforeCaret.length ||
      (allDigits ? formatTurkishMobileDisplay(allDigits).length : (emptyDisplayValue || '').length);

    // Re-render sonrası caret pozisyonunu geri koy
    window.requestAnimationFrame(() => {
      if (inputRef.current) {
        try {
          inputRef.current.setSelectionRange(nextPos, nextPos);
        } catch {
          // ignore invalid selection errors
        }
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const prefixLen = FIXED_PREFIX.length;
    const target = e.currentTarget;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? start;

    // Son caret pozisyonunu sakla (10 hane doluyken ekstra input'u yok sayarken kullanmak için)
    lastCaretRef.current = start;

    // Prefix alanında backspace/delete ile değişikliğe izin verme
    const isBackspaceOnPrefix = e.key === 'Backspace' && start <= prefixLen && end <= prefixLen;
    const isDeleteOnPrefix = e.key === 'Delete' && start < prefixLen;

    if (isBackspaceOnPrefix || isDeleteOnPrefix) {
      e.preventDefault();
      window.requestAnimationFrame(() => {
        try {
          target.setSelectionRange(prefixLen, prefixLen);
        } catch {
          // ignore
        }
      });
    }
  };

  return (
    <TextField
      {...props}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      inputRef={inputRef}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 18,
        autoComplete: 'tel',
        ...inputProps,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          handleKeyDown(e);
          if (inputProps && typeof inputProps.onKeyDown === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (inputProps as any).onKeyDown(e);
          }
        },
      }}
    />
  );
};

export default TurkishMobilePhoneInput;
